import { Component, OnInit, Input, OnChanges, SimpleChanges, AfterViewInit, Output, EventEmitter, OnDestroy, ContentChild, TemplateRef, ElementRef } from '@angular/core';
import { Listing } from '../../../classes/listing';
import { SCREEN_SIZE } from '../../../classes/enums';
import { CommunicationService } from '../../../services/communication.service';
import { HasSubscriptions } from '../../../components/has-subscriptions';
import { DomManipulationService } from '../../../services/dom-manipulation/dom-manipulation.service';
import { SWIPER_SCRIPT } from '../../../classes/dynamicScripts';
import {delay} from "rxjs/operators";

declare var Swiper: any;
@Component({
  selector: 'app-item-swiper',
  templateUrl: './item-swiper.component.html',
  styleUrls: ['./item-swiper.component.scss']
})
export class ItemSwiperComponent extends HasSubscriptions implements AfterViewInit, OnChanges, OnDestroy {

  // When we're displaying it in the card preview, we want to do some special things from a displaying perspective
  @Input() isForCardPreview: boolean = false;
  @Input() isLandingPageListing: boolean = false;
  @Input() isListingDetailPage: boolean = false;

  @Input() items: Listing[] = [];
  @Input() hasFinishedLoadingItems: boolean = false;
  @Input() showItemCount: boolean = true;

  // If we want to start the swiper at a specific index when initializing (ex: when on mobile map, selected a listing on the card preview, going to LDP, and then back to search)
  @Input() selectedListingIndex: string = '';

  // We need this separate because paginated items does not indicate the full list of items
  @Input() totalItems: number;

  @Output() itemClicked = new EventEmitter<any>();
  @Output() itemFocused = new EventEmitter<any>();

  @Output() slideChanged = new EventEmitter<any>();

  screenSize: SCREEN_SIZE = SCREEN_SIZE.MOBILE_ONLY;

  // Enum
  SCREEN_SIZE = SCREEN_SIZE;

  Listing = Listing;
  swiper: Swiper;

  private _shouldResetToBeginningOfSlides: boolean = false;

  constructor(
    private _communicationService: CommunicationService,
    private _domManipulationService: DomManipulationService,
  ) {
    super();
  }

  ngAfterViewInit() {
    this.addSub(this._communicationService.screenSize.pipe(delay(0)).subscribe((s) => {
      this.screenSize = s;
      // We need to reinitialize the swiper on screen size changes since certain properties/event handlers need to be reset to have different properties
      if (this.swiper) this.initSwiper();
    }));
  }

  ngOnChanges(changes: SimpleChanges) {

    /* If new items come in, we need to check if they are either 2 situations:
    *  1) We're moving away to a new page so the items are being completely reset
    *  2) We're paginating so we're adding on to the existing set of items
    */

    if (changes.items) {
      const itemChanges = changes.items;
      // Case 1
      if ((!itemChanges.previousValue || itemChanges.previousValue.length === 0) && itemChanges.currentValue.length > 0) {
        // We need to trigger a reset of the slides to go back to the beginning
        if (!itemChanges.firstChange) this._shouldResetToBeginningOfSlides = true;
        this.initSwiper();
      }
      // Case 2
      else if (itemChanges.currentValue.length > 0 && itemChanges.previousValue.length > itemChanges.currentValue.length) {
        this.swiper.update();
      }

    }
  }

  destroySwiper() {
    if (this.swiper && this.swiper.destroy) {
      this.swiper.destroy(true, true);
      // even though we're calling destroy on the swiper instance, this is a safeguard against
      // retaining any references
      this.swiper = undefined;
    }
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.destroySwiper();
  }

  resetSlidesToBeginning() {
    if (this._shouldResetToBeginningOfSlides && this.swiper) {
      this._shouldResetToBeginningOfSlides = false;
      const initialItemIndex = this.items.map((r) => r.listingId).indexOf( this.selectedListingIndex) ;
      this.swiper.slideTo(initialItemIndex !== -1 ? initialItemIndex : 0);
    }
  }

  initSwiper() {
    this._domManipulationService.loadScript(SWIPER_SCRIPT).subscribe(() => {
      this.destroySwiper();
      const swiperConfigToUse = this.screenSize === SCREEN_SIZE.MOBILE_ONLY ? swiperConfigMobile : swiperConfigDesktop;

      // HACK: When using observer set to true, and setting slidesPerView auto, on init will go to the end of the slides for some reason
      // https://github.com/nolimits4web/swiper/issues/2399
      swiperConfigToUse.on = {
        slideChange: () => {
          this.resetSlidesToBeginning();
          this.slideChanged.emit({ card: this.items[this.swiper.activeIndex], index: this.swiper.activeIndex });
        }
      }
      this.swiper = new Swiper('.swiper-container', swiperConfigToUse);
      if (this.selectedListingIndex) {
        this._shouldResetToBeginningOfSlides = true;
        this.resetSlidesToBeginning();
      }
    });
  }
}

const baseSwiperConfig: SwiperOptions = {
  grabCursor: true,
  observer: true,
  initialSlide: 0,
  preventClicks: true,
  preventClicksPropagation: true,
  slideToClickedSlide: false,
  keyboard: {
    enabled: true
  },
  preloadImages: false,
  updateOnImagesReady: false,
};

const swiperConfigMobile: SwiperOptions = Object.assign({
  effect: 'coverflow',
  centeredSlides: true,
  slidesPerView: 1.2,
  slidesOffsetBefore: 0,
  slidesOffsetAfter: 0,
  spaceBetween: 10,
  coverflowEffect: {
    rotate: 30,
    stretch: 0,
    depth: 50,
    modifier: 1,
    slideShadows: true,
  },
  resistance: false,
  followFinger: true,
  touchRatio: 1.75,
}, baseSwiperConfig);

const swiperConfigDesktop: SwiperOptions = Object.assign({
  centeredSlides: false,
  slidesPerView: 'auto',
  slidesOffsetBefore: 20,
  slidesOffsetAfter: 20,
  spaceBetween: 35,
  grabCursor: true,
}, baseSwiperConfig);
